import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { BrutalButton } from "@/components/ui/BrutalButton";
import { BrutalBadge } from "@/components/ui/BrutalBadge";
import { Mail } from "lucide-react";
import DOMPurify from "dompurify";

interface EmailTemplate {
  id: string;
  template_key: string;
  template_name: string;
  category: string;
  is_active: boolean;
  variables: any;
  subject_it: string;
  subject_en: string;
  subject_es: string;
  body_html_it: string;
  body_html_en: string;
  body_html_es: string;
  body_text_it: string | null;
  body_text_en: string | null;
  body_text_es: string | null;
}

interface EmailPreviewModalProps {
  template: EmailTemplate;
  open: boolean;
  onClose: () => void;
}

// Preview variables for template rendering
const PREVIEW_VARIABLES: Record<string, string> = {
  siteUrl: "https://inclusia.pro",
  firstName: "Mario",
  lastName: "Rossi",
  fullName: "Mario Rossi",
  email: "mario.rossi@esempio.it",
  actionUrl: "https://inclusia.pro/action",
  eventTitle: "Evento di Esempio",
  eventDate: new Date().toLocaleDateString("it-IT"),
  eventLocation: "Milano, Italia",
  notificationTitle: "Titolo Notifica",
  notificationDescription: "Descrizione della notifica di esempio.",
  notificationCtaText: "Scopri di più",
  notificationCtaUrl: "https://inclusia.pro",
  notificationImageUrl: "https://via.placeholder.com/600x300",
  qrCode: "https://via.placeholder.com/200",
  // Job-related variables
  applicantName: "Laura Bianchi",
  applicantEmail: "laura.bianchi@esempio.it",
  applicantPhone: "+39 333 1234567",
  jobTitle: "Frontend Developer Senior",
  companyName: "Tech Inclusiva S.r.l.",
  jobId: "abc123-job-id",
  jobUrl: "https://inclusia.pro/jobs/frontend-developer-senior",
  dashboardUrl: "https://inclusia.pro/dashboard/company/jobs",
  applicationsUrl: "https://inclusia.pro/dashboard/company/jobs/abc123-job-id/applications",
  expiresAt: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString("it-IT"),
  extendUrl: "https://inclusia.pro/dashboard/company/jobs?action=extend&job_id=abc123-job-id",
  renewUrl: "https://inclusia.pro/dashboard/company/jobs?action=extend&job_id=abc123-job-id",
  newExpiresAt: new Date(Date.now() + 60 * 24 * 60 * 60 * 1000).toLocaleDateString("it-IT"),
  coverLetter: "Sono interessata alla posizione e credo che le mie competenze...",
};

// Function to replace template placeholders with preview values
const renderTemplate = (text: string): string => {
  if (!text) return "";
  return text.replace(/\{\{(\w+)\}\}/g, (match, key) => 
    PREVIEW_VARIABLES[key] || match
  );
};

export function EmailPreviewModal({ template, open, onClose }: EmailPreviewModalProps) {
  const { t } = useTranslation();
  const [activeLanguage, setActiveLanguage] = useState<'it' | 'en' | 'es'>('it');
  const [activeFormat, setActiveFormat] = useState<'html' | 'text'>('html');

  const currentSubject = renderTemplate(template[`subject_${activeLanguage}`]);
  const currentBodyHtml = renderTemplate(template[`body_html_${activeLanguage}`]);
  const currentBodyText = template[`body_text_${activeLanguage}`] 
    ? renderTemplate(template[`body_text_${activeLanguage}`]) 
    : null;

  const sanitizedHtml = DOMPurify.sanitize(currentBodyHtml, {
    ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 'a', 'img', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li', 'span', 'div', 'table', 'thead', 'tbody', 'tr', 'td', 'th'],
    ALLOWED_ATTR: ['href', 'src', 'alt', 'style', 'class', 'width', 'height', 'colspan', 'rowspan']
  });

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl flex items-center gap-2">
            <Mail className="h-5 w-5 text-primary" />
            {t("admin.settings.email.templates.preview.title") || "Anteprima Email"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Language selector and Subject section */}
          <div className="py-3 px-4 bg-surface/50 border-2 border-dark">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-bold">
                {t("admin.settings.email.templates.preview.language") || "Lingua"}:
              </span>
              <div className="flex gap-2">
                {(['it', 'en', 'es'] as const).map((lang) => (
                  <BrutalBadge
                    key={lang}
                    variant={activeLanguage === lang ? "default" : "secondary"}
                    className="cursor-pointer"
                    onClick={() => setActiveLanguage(lang)}
                  >
                    {lang.toUpperCase()}
                  </BrutalBadge>
                ))}
              </div>
            </div>
            <div className="flex items-baseline gap-2 border-t-2 border-dark pt-3">
              <span className="text-sm font-bold text-muted-foreground">
                {t("admin.settings.email.templates.preview.subject") || "Oggetto"}:
              </span>
              <span className="text-base font-normal">{currentSubject}</span>
            </div>
          </div>

          {/* Body section with tabs */}
          <div className="space-y-3">
            <Tabs value={activeFormat} onValueChange={(v) => setActiveFormat(v as 'html' | 'text')}>
              <TabsList>
                <TabsTrigger value="html">
                  {t("admin.settings.email.templates.preview.tabHtml") || "HTML"}
                </TabsTrigger>
                <TabsTrigger value="text">
                  {t("admin.settings.email.templates.preview.tabText") || "Testo"}
                </TabsTrigger>
              </TabsList>

              <TabsContent value="html" className="mt-4">
                <div className="bg-card border-2 border-dark shadow-[4px_4px_0px_0px] shadow-dark p-6 min-h-[300px]">
                  <div
                    dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
                    className="prose prose-sm max-w-none"
                  />
                </div>
              </TabsContent>

              <TabsContent value="text" className="mt-4">
                {currentBodyText ? (
                  <pre className="bg-surface/50 border-2 border-dark p-4 font-mono text-sm whitespace-pre-wrap overflow-x-auto min-h-[300px]">
                    {currentBodyText}
                  </pre>
                ) : (
                  <div className="bg-surface/50 border-2 border-dark p-8 text-center text-muted-foreground min-h-[300px] flex items-center justify-center">
                    <p>{t("admin.settings.email.templates.preview.noContent") || "Nessun contenuto testo disponibile"}</p>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
